import React, { useCallback, useEffect, useState } from 'react';
import { cx } from 'linaria';
import { useDispatch, useSelector } from 'react-redux';
import { LinkButton, Typography } from 'fronton-react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@fronton/icons-react';
import { analyticsUtils } from 'utils';
import {
  brandLink,
  brandLinkSliced,
  container,
  heading,
  listContent,
  listContentByChar,
  showMore,
} from './styles';
import { Brand } from '../all-brands/types';

import { getBrandsFilter } from '../brands-filter/ducks/selectors';
import { setBrandsFilter } from '../brands-filter/ducks/brands-filter-slice';
import { defineGroupByChar } from '../all-brands/ducks/get-brands';

type Props = {
  title: string;
  items: Brand[];
  scrollCallback: () => void;
};

export const DATA_QA_BRAND_LIST_LINK = 'brand-list-link';

export const BrandsListItem: React.FC<Props> = ({ title, items, scrollCallback }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const text = t('ui.accordion');
  const [activeGroup, activeChar] = useSelector(getBrandsFilter);
  const [group, setGroup] = useState(activeGroup || '');

  useEffect(() => {
    setGroup(defineGroupByChar(title));
  }, [title]);

  const handleCharPick = useCallback(
    (value: string) => {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock: 'brandList / list', // блок, в котором расположен элемент
        elementName: value, // название блока (заголовок)
        elementText: `${text} ${value}`, // название бренда
      });

      dispatch(setBrandsFilter([group, value]));
      scrollCallback();
    },
    [dispatch, group, scrollCallback, text],
  );

  const gaCallback = useCallback(
    (label: string) => {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock: 'brandList / list', // блок, в котором расположен элемент
        elementName: title, // название блока (заголовок)
        elementText: label, // название бренда
      });
    },
    [title],
  );

  return (
    <div className={container} data-qa="brands-list-item">
      <Typography variant="h3" className={heading} data-qa="brands-list-item-title">
        {title}
      </Typography>
      {!activeChar ? (
        <div className={listContent}>
          {items.map(({ label, link }, index) => (
            <a
              key={title + index}
              href={link}
              className={cx(brandLinkSliced, brandLink)}
              onClick={() => gaCallback(label)}
              data-qa={DATA_QA_BRAND_LIST_LINK}
            >
              <Typography variant="m" size="body_long">
                {label}
              </Typography>
            </a>
          ))}
          {items.length > 6 && (
            <LinkButton
              iconRight={<ChevronRightIcon type="outline" size="l" color="text-primary" />}
              onClick={() => handleCharPick(title)}
              size="m"
              variant="normal"
              className={showMore}
            >
              {text} {title}
            </LinkButton>
          )}
        </div>
      ) : (
        <div className={listContentByChar}>
          {items.map(({ label, link }, index) => (
            <a
              key={title + index}
              href={link}
              className={brandLink}
              onClick={() => gaCallback(label)}
              data-qa={DATA_QA_BRAND_LIST_LINK}
            >
              <Typography variant="m" size="body_long">
                {label}
              </Typography>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
